import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form,
  FormFeedback,
  Input,
  Button,
  Label,
  Alert,
  Spinner,
  NavItem,
  Nav,
  NavLink,
} from "reactstrap";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import axios from "axios";
import AuthService from "services/authService";
import classNames from "classnames";
import logo from "../../assets/images/logo.png";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("club");

  // Track the current step in the password reset flow - now just request or reset
  const [resetStep, setResetStep] = useState("request");
  const [userEmail, setUserEmail] = useState("");

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setError("");
      setSuccess("");
    }
  };
  const handleResetChange = (e) => {
    setError(""); // clear any old error
    resetPasswordValidation.handleChange(e); // let Formik update its value
  };
  const resendCode = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (activeTab === "club") {
        await AuthService.forgotPasswordClub(userEmail);
      } else {
        await AuthService.forgotPasswordAgent(userEmail);
      }
      setSuccess(t("forgotPassword.codeSent"));
    } catch (err) {
      setError(err.response?.data?.message || t("forgotPassword.errorMessage"));
    } finally {
      setLoading(false);
    }
  };
  // Forgot Password Form (Step 1: Request reset)
  const forgotPasswordValidation = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("validation.validEmail"))
        .required(t("validation.emailRequired")),
    }),
    onSubmit: async (values) => {
      try {
        console.log(
          `Submitting ${activeTab} forgot password request for:`,
          values.email
        );

        setLoading(true);
        setError("");

        // Use different service based on active tab
        if (activeTab === "club") {
          await AuthService.forgotPasswordClub(values.email);
        } else {
          await AuthService.forgotPasswordAgent(values.email);
        }

        // Store email for next steps and move directly to reset password step
        setUserEmail(values.email);
        setResetStep("reset");
        setSuccess(t("forgotPassword.codeSent"));
        forgotPasswordValidation.resetForm();
      } catch (error) {
        console.error("Error during forgot password request:", error);
        setError(
          error.response?.data?.message || t("forgotPassword.errorMessage")
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const resetPasswordValidation = useFormik({
    initialValues: {
      code: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .required(t("verification.codeRequired"))
        .min(4, t("verification.codeLength"))
        .max(8, t("verification.codeLength")),
      password: Yup.string()
        .min(8, t("validation.passwordLength"))
        .required(t("validation.passwordRequired")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t("validation.passwordMatch"))
        .required(t("validation.confirmPasswordRequired")),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError("");
        console.log(`Resetting password for ${activeTab} user:`, userEmail);

        if (token) {
          await axios.post("/api/admin/resetPassword", {
            token: token,
            password: values.password,
          });
        } else {
          if (activeTab === "club") {
            await AuthService.resetPasswordClub({
              code: values.code,
              password: values.password,
            });
          } else {
            await AuthService.resetPasswordAgent({
              code: values.code,
              password: values.password,
            });
          }
        }
        setSuccess(t("resetPassword.success"));
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } catch (error) {
        setError(
          error.response?.data?.message || t("resetPassword.tokenInvalid")
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Render the appropriate form based on current step
  const renderForgotPasswordForm = () => {
    if (token) {
      // Show reset password form directly if token is in URL (without code field)
      return renderResetPasswordForm(true);
    } else if (resetStep === "request") {
      // Step 1: Request password reset
      return (
        <>
          <Nav tabs className="nav-tabs-custom nav-justified">
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classNames({
                  active: activeTab === "club",
                })}
                onClick={() => {
                  toggleTab("club");
                }}
              >
                <span className="d-block d-sm-none">
                  <i className="fas fa-home"></i>
                </span>
                <span className="d-none d-sm-block">{t("common.club")}</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classNames({
                  active: activeTab === "agent",
                })}
                onClick={() => {
                  toggleTab("agent");
                }}
              >
                <span className="d-block d-sm-none">
                  <i className="far fa-user"></i>
                </span>
                <span className="d-none d-sm-block">{t("common.agent")}</span>
              </NavLink>
            </NavItem>
          </Nav>

          <Form
            className="form-horizontal mt-4"
            onSubmit={(e) => {
              e.preventDefault();
              forgotPasswordValidation.handleSubmit();
              return false;
            }}
          >
            <div className="mb-3">
              <Label className="form-label">{t("forgotPassword.email")}</Label>
              <Input
                name="email"
                className="form-control"
                placeholder={t("forgotPassword.enterEmail")}
                type="email"
                onChange={forgotPasswordValidation.handleChange}
                onBlur={forgotPasswordValidation.handleBlur}
                value={forgotPasswordValidation.values.email}
                invalid={
                  forgotPasswordValidation.touched.email &&
                  forgotPasswordValidation.errors.email
                    ? true
                    : false
                }
              />
              {forgotPasswordValidation.touched.email &&
              forgotPasswordValidation.errors.email ? (
                <FormFeedback type="invalid">
                  {forgotPasswordValidation.errors.email}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mt-3 d-grid">
              {loading ? (
                <Button color="primary" disabled>
                  <Spinner size="sm" className="me-2" />
                  {t("loading")}
                </Button>
              ) : (
                <Button color="primary" type="submit">
                  {t("forgotPassword.sendLinkButton")}
                </Button>
              )}
            </div>
          </Form>
        </>
      );
    } else {
      // Step 2: Combined verification code and password reset
      return renderResetPasswordForm(false);
    }
  };

  // Render reset password form - with optional code field
  const renderResetPasswordForm = (isTokenBased) => {
    return (
      <Form
        className="form-horizontal"
        onSubmit={(e) => {
          e.preventDefault();
          resetPasswordValidation.handleSubmit();
          return false;
        }}
      >
        {!isTokenBased && (
          <>
            <div className="text-center mb-4">
              <p>{t("verification.instructions", { email: userEmail })}</p>
            </div>

            <div className="mb-3">
              <Label className="form-label">{t("verification.code")}</Label>
              <Input
                name="code"
                className="form-control"
                placeholder={t("verification.enterCode")}
                onChange={handleResetChange}
                onBlur={resetPasswordValidation.handleBlur}
                value={resetPasswordValidation.values.code}
                invalid={
                  resetPasswordValidation.touched.code &&
                  resetPasswordValidation.errors.code
                    ? true
                    : false
                }
              />
              {resetPasswordValidation.touched.code &&
              resetPasswordValidation.errors.code ? (
                <FormFeedback type="invalid">
                  {resetPasswordValidation.errors.code}
                </FormFeedback>
              ) : null}
            </div>
          </>
        )}

        <div className="mb-3">
          <Label className="form-label">{t("resetPassword.newPassword")}</Label>
          <Input
            name="password"
            type="password"
            placeholder={t("resetPassword.enterNewPassword")}
            onChange={handleResetChange}
            onBlur={resetPasswordValidation.handleBlur}
            value={resetPasswordValidation.values.password}
            invalid={
              resetPasswordValidation.touched.password &&
              resetPasswordValidation.errors.password
                ? true
                : false
            }
          />
          {resetPasswordValidation.touched.password &&
          resetPasswordValidation.errors.password ? (
            <FormFeedback type="invalid">
              {resetPasswordValidation.errors.password}
            </FormFeedback>
          ) : null}
        </div>

        <div className="mb-3">
          <Label className="form-label">
            {t("resetPassword.confirmPassword")}
          </Label>
          <Input
            name="confirmPassword"
            type="password"
            placeholder={t("resetPassword.confirmNewPassword")}
            onChange={handleResetChange}
            onBlur={resetPasswordValidation.handleBlur}
            value={resetPasswordValidation.values.confirmPassword}
            invalid={
              resetPasswordValidation.touched.confirmPassword &&
              resetPasswordValidation.errors.confirmPassword
                ? true
                : false
            }
          />
          {resetPasswordValidation.touched.confirmPassword &&
          resetPasswordValidation.errors.confirmPassword ? (
            <FormFeedback type="invalid">
              {resetPasswordValidation.errors.confirmPassword}
            </FormFeedback>
          ) : null}
        </div>

        <div className="mt-3 d-grid">
          {loading ? (
            <Button color="primary" disabled>
              <Spinner size="sm" className="me-2" />
              {t("loading")}
            </Button>
          ) : (
            <Button color="primary" type="submit">
              {t("resetPassword.resetButton")}
            </Button>
          )}
        </div>

        {!isTokenBased && (
          <div className="mt-3 text-center">
            <p className="text-muted mb-0">
              {t("verification.didntReceive")}{" "}
              <a href="#" className="text-primary" onClick={resendCode}>
                {t("verification.resendCode")}{" "}
              </a>
            </p>
          </div>
        )}
      </Form>
    );
  };

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary mb-20">
                  <div className="text-primary text-center p-4">
                    <h5 className="text-white font-size-20">
                      {token || resetStep === "reset"
                        ? t("resetPassword.title")
                        : t("forgotPassword.title")}
                    </h5>
                    <p className="text-white-50">
                      {resetStep === "request"
                        ? t("forgotPassword.description")
                        : t("resetPassword.description")}
                    </p>
                    <Link to="/" className="logo logo-admin">
                      <img src={logo} height="55" alt="logo" />
                    </Link>
                  </div>
                </div>
                <CardBody className="pt-[999px]">
                  <div className="p-2">
                    {success && <Alert color="success">{success}</Alert>}
                    {renderForgotPasswordForm()}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  {t("forgotPassword.backToLogin")}{" "}
                  <Link to="/login" className="fw-medium text-primary">
                    {t("forgotPassword.loginLink")}
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ForgotPassword;
